import { useRef } from 'react';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';
import { RouterLink } from '@redux-first-router-link';

import MoreHorizontalSolid from '@simplywallst/ui-core/icons/MoreHorizontalSolid';
import SupportSolid from '@simplywallst/ui-core/icons/SupportSolid';
import { ButtonWithDropdown, Text, Grid } from '@simplywallst/ui-core';

import { formatterPercentage } from '@/utilities/format';
import PositiveNegativeValue from '@components/PositiveNegativeValue';

import { useTypeformDrawer } from '@/hooks/useTypeformDrawer';

import { Header, SnowflakeWrapper, More } from './styled';

import type { Company } from './types';

const Snowflake = loadable(() => import('@/components/DeprecatedSnowflake'));

interface Props {
  company: Company;
  onClick?: () => void;
}

export const FeedCardHeader = ({ company, onClick }: Props) => {
  const {
    name,
    tickerSymbol,
    scores,
    return7D,
    return1YrAbs,
    sharePriceFormatted,
    canonicalUrl,
  } = company;
  const { trackEvent } = useTracking();
  const makeTypeformPopup = useTypeformDrawer('RveL1l', {
    company: name,
  });
  const options = useRef([
    { label: 'Feedback', value: 'FEEDBACK', icon: <SupportSolid /> },
  ]).current;

  const handleClick = () => {
    trackEvent({
      action: 'click',
      modifier: 'more',
    });
  };
  return (
    <>
      <RouterLink noUnderline={true} to={canonicalUrl} onClick={onClick}>
        <Header
          gap="12px"
          templateColumns="min-content auto"
          alignItems="start"
        >
          <SnowflakeWrapper>
            <Snowflake points={scores || []} disableLabel={true} />
          </SnowflakeWrapper>
          <Grid
            templateColumns="max-content auto"
            justifyContent="space-between"
          >
            <Grid rowGap="8px">
              <div>
                <Grid templateColumns="repeat(2, min-content)" gap="8px">
                  <Text
                    color="brand01"
                    typography="xSmallStrong"
                    data-cy-id="dashboard-feed-card-ticker"
                  >
                    {tickerSymbol}
                  </Text>
                  <Text
                    color="whiteRGBA"
                    opacity={0.7}
                    typography="xSmallStrong"
                    data-cy-id="dashboard-feed-card-price"
                  >
                    {sharePriceFormatted}
                  </Text>
                </Grid>
                <Text
                  typography="tiny"
                  color="whiteRGBA"
                  opacity={0.5}
                  isTruncated
                  data-cy-id="dashboard-feed-card-name"
                >
                  {name}
                </Text>
              </div>
              <Grid templateColumns="repeat(2, min-content)" gap="8px">
                <Grid templateColumns="repeat(2, min-content)" gap="4px">
                  <Text
                    as="span"
                    color="whiteRGBA"
                    typography="xSmall"
                    opacity={0.5}
                  >
                    7D{' '}
                  </Text>
                  <Text typography="xSmall">
                    <PositiveNegativeValue
                      value={return7D}
                      data-cy-id="dashboard-feed-card-return7D"
                    >
                      {formatterPercentage(return7D)}
                    </PositiveNegativeValue>
                  </Text>
                </Grid>
                <Grid templateColumns="repeat(2, min-content)" gap="4px">
                  <Text
                    as="span"
                    color="whiteRGBA"
                    typography="xSmall"
                    opacity={0.5}
                  >
                    1Y{' '}
                  </Text>
                  <Text typography="xSmall">
                    <PositiveNegativeValue
                      value={return1YrAbs}
                      data-cy-id="dashboard-feed-card-return1Y"
                    >
                      {formatterPercentage(return1YrAbs)}
                    </PositiveNegativeValue>
                  </Text>
                </Grid>
              </Grid>
            </Grid>
            <More>
              <ButtonWithDropdown
                name="more"
                styleType="gentle"
                data-cy-id="dashboard-feed-card-more"
                options={options}
                onSelect={makeTypeformPopup}
                onClick={handleClick}
              >
                <MoreHorizontalSolid />
              </ButtonWithDropdown>
            </More>
          </Grid>
        </Header>
      </RouterLink>
    </>
  );
};
